// ==================== PLATFORM HANDLERS ====================
import { backendUrl } from "../api/constants";

/**
 * Base class for media platform handlers
 */
class MediaPlatformHandler {
  constructor() {
    this.platform = '';
  }

  /**
   * Check if URL belongs to this platform
   * @param {string} url - URL to check
   * @returns {boolean}
   */
  isValidUrl(url) {
    throw new Error('isValidUrl must be implemented');
  }

  /**
   * Extract media ID from URL
   * @param {string} url - Platform URL
   * @returns {string|null} - Media ID or null
   */
  extractId(url) {
    throw new Error('extractId must be implemented');
  }

  /**
   * Get embed URL for the media
   * @param {string} id - Media ID
   * @returns {string} - Embed URL
   */
  getEmbedUrl(id) {
    throw new Error('getEmbedUrl must be implemented');
  }

  /**
   * Get thumbnail URL for the media
   * @param {string} id - Media ID
   * @returns {string} - Thumbnail URL
   */
  getThumbnailUrl(id) {
    throw new Error('getThumbnailUrl must be implemented');
  }

  /**
   * Get platform icon component
   * @returns {JSX.Element} - Icon component
   */
  getIcon() {
    throw new Error('getIcon must be implemented');
  }

  /**
   * Get platform colors/styling
   * @returns {object} - Styling object
   */
  getStyling() {
    return {
      primary: '#000000',
      accent: '#ffffff'
    };
  }
}

/**
 * YouTube platform handler
 */
class YouTubeHandler extends MediaPlatformHandler {
  constructor() {
    super();
    this.platform = 'youtube';
  }

  isValidUrl(url) {
    if (!url) return false;
    return /(?:youtube\.com|youtu\.be)/i.test(url);
  }

  /**
   * Detect the type of YouTube URL
   * @param {string} url - YouTube URL
   * @returns {object} - Object with type and data
   */
  detectUrlType(url) {
    if (!url) return { type: 'unknown', data: null };

    // Channel URLs (profiles) - should NOT be embedded
    const channelPatterns = [
      /youtube\.com\/@([^\/\?\#]+)/,           // @username format
      /youtube\.com\/c\/([^\/\?\#]+)/,         // /c/channelname format
      /youtube\.com\/channel\/([^\/\?\#]+)/,   // /channel/UC... format
      /youtube\.com\/user\/([^\/\?\#]+)/,      // Legacy /user/ format
    ];

    for (const pattern of channelPatterns) {
      const match = url.match(pattern);
      if (match && match[1]) {
        return { type: 'channel', data: match[1] };
      }
    }

    // Check if it's a profile/about/community page (non-embeddable)
    if (/youtube\.com\/[^\/]+\/(about|community|channels|playlists|videos|shorts|streams|featured)/i.test(url)) {
      return { type: 'profile-section', data: null };
    }

    // Playlist URLs
    const playlistMatch = url.match(/[?&]list=([^&\n?#]+)/);
    if (playlistMatch && playlistMatch[1]) {
      // Check if it's a video in a playlist (has both video and playlist)
      const videoInPlaylist = url.match(/[?&]v=([^&\n?#]+)/);
      if (videoInPlaylist && videoInPlaylist[1]) {
        return { 
          type: 'video-in-playlist', 
          data: { 
            videoId: videoInPlaylist[1], 
            playlistId: playlistMatch[1] 
          } 
        };
      }
      return { type: 'playlist', data: playlistMatch[1] };
    }

    // Video URLs (all embeddable types)
    const videoPatterns = [
      { regex: /youtube\.com\/live\/([^&\n?#]+)/, type: 'live' },           // Live streams
      { regex: /youtube\.com\/shorts\/([^&\n?#]+)/, type: 'shorts' },       // Shorts
      { regex: /youtube\.com\/watch\?v=([^&\n?#]+)/, type: 'video' },       // Regular watch
      { regex: /youtube\.com\/watch\?.*v=([^&\n?#]+)/, type: 'video' },     // Watch with params
      { regex: /youtu\.be\/([^&\n?#]+)/, type: 'video' },                   // Short links
      { regex: /youtube\.com\/embed\/([^&\n?#]+)/, type: 'video' },         // Embed URLs
      { regex: /youtube\.com\/v\/([^&\n?#]+)/, type: 'video' },             // Legacy /v/ format
    ];

    for (const { regex, type } of videoPatterns) {
      const match = url.match(regex);
      if (match && match[1]) {
        return { type, data: match[1] };
      }
    }

    return { type: 'unknown', data: null };
  }

  async extractId(url) {
    if (!url) return null;
    
    const urlInfo = this.detectUrlType(url);
    
    // Return null for non-embeddable types (will fallback to SocialButton)
    if (urlInfo.type === 'channel' || urlInfo.type === 'profile-section' || urlInfo.type === 'unknown') {
      return null;
    }

    // Handle embeddable types
    switch (urlInfo.type) {
      case 'video':
      case 'live':
      case 'shorts':
        return urlInfo.data; // Video ID
      
      case 'playlist':
        return `playlist_${urlInfo.data}`; // Prefix to distinguish playlists
      
      case 'video-in-playlist':
        return `video_${urlInfo.data.videoId}_list_${urlInfo.data.playlistId}`;
      
      default:
        return null;
    }
  }

  getEmbedUrl(mediaId) {
    if (!mediaId) return null;

    // Use backend proxy to avoid YouTube error 153
    // Handle playlist embeds
    if (mediaId.startsWith('playlist_')) {
      const playlistId = mediaId.replace('playlist_', '');
      return `${backendUrl}/embed/videoseries?list=${playlistId}`;
    }

    // Handle video in playlist
    if (mediaId.includes('_list_')) {
      const parts = mediaId.split('_list_');
      const videoId = parts[0].replace('video_', '');
      const playlistId = parts[1];
      return `${backendUrl}/embed/${videoId}?list=${playlistId}`;
    }

    // Regular video/live/shorts - all use the same embed format via backend proxy
    return `${backendUrl}/embed/${mediaId}`;
  }

  getThumbnailUrl(mediaId, quality = 'hqdefault') {
    if (!mediaId) return null;

    // For playlists, we can't get a direct thumbnail
    if (mediaId.startsWith('playlist_')) {
      return null; // Will use gradient fallback
    }

    // Extract video ID if it's a video-in-playlist
    let videoId = mediaId;
    if (mediaId.includes('_list_')) {
      videoId = mediaId.split('_list_')[0].replace('video_', '');
    }

    return `https://img.youtube.com/vi/${videoId}/${quality}.jpg`;
  }

  getStyling() {
    return {
      primary: '#ff0000',
      accent: '#ffffff',
      gradient: 'linear-gradient(135deg, #ff0000, #cc0000)'
    };
  }
}

/**
 * SoundCloud platform handler
 */
class SoundCloudHandler extends MediaPlatformHandler {
  constructor() {
    super();
    this.platform = 'soundcloud';
  }

  isValidUrl(url) {
    if (!url) return false;
    return /(?:soundcloud\.com|on\.soundcloud\.com)/i.test(url);
  }

  async extractId(url) {
    // SoundCloud uses the full URL as the "ID" for embedding
    if (!this.isValidUrl(url)) return null;
    
    // For short URLs, resolve via backend using centralized axios
    if (url.includes('on.soundcloud.com')) {
      try {
        // Import axiosInstance dynamically to avoid circular imports
        const axiosModule = await import('../api/axiosInstance');
        const axiosInstance = axiosModule.default;
        
        // Ensure we have the axios instance before making the request
        if (!axiosInstance) {
          throw new Error('axiosInstance not available');
        }
        
        console.log('Making request with axiosInstance baseURL:', axiosInstance.defaults.baseURL);
        const { data } = await axiosInstance.get(`/api/resolve-url?url=${encodeURIComponent(url)}`);
        return data.resolvedUrl;
      } catch (error) {
        console.error('Failed to resolve SoundCloud short URL:', error);
      }
    }
    
    return url;
  }


  getEmbedUrl(trackUrl) {
    return `https://w.soundcloud.com/player/?url=${encodeURIComponent(trackUrl)}&color=%23ff5500&auto_play=false&hide_related=true&show_comments=true&show_user=true&show_reposts=false&show_teaser=true`;
  }

  getThumbnailUrl(trackUrl) {
    // SoundCloud doesn't have a direct thumbnail URL API
    // We'll use a generic SoundCloud thumbnail or fetch from their API
    return null; // Will trigger fallback gradient
  }

  getStyling() {
    return {
      primary: '#ff5500',
      accent: '#ff7700',
      gradient: 'linear-gradient(135deg, #ff5500, #ff7700, #ff9900)'
    };
  }
}

/**
 * Spotify platform handler
 */
class SpotifyHandler extends MediaPlatformHandler {
  constructor() {
    super();
    this.platform = 'spotify';
  }

  isValidUrl(url) {
    if (!url) return false;
    return /(?:spotify\.com|open\.spotify\.com)/i.test(url);
  }

  async extractId(url) {
    if (!url) return null;
    
    // First, check if this is already an embed URL and extract the path from it
    const embedMatch = url.match(/(?:open\.)?spotify\.com\/embed\/([^?\s&]+)/);
    if (embedMatch && embedMatch[1]) {
      // Return the path from the embed URL (e.g., "artist/397xJtr5O1PQm7SFyA0obz")
      return embedMatch[1];
    }
    
    const patterns = [
      // Track URLs
      /(?:open\.spotify\.com\/track\/)([^?\s&]+)/,
      /(?:spotify\.com\/track\/)([^?\s&]+)/,
      // Album URLs  
      /(?:open\.spotify\.com\/album\/)([^?\s&]+)/,
      /(?:spotify\.com\/album\/)([^?\s&]+)/,
      // Playlist URLs
      /(?:open\.spotify\.com\/playlist\/)([^?\s&]+)/,
      /(?:spotify\.com\/playlist\/)([^?\s&]+)/,
      // Artist URLs
      /(?:open\.spotify\.com\/artist\/)([^?\s&]+)/,
      /(?:spotify\.com\/artist\/)([^?\s&]+)/,
      // Episode URLs (podcasts)
      /(?:open\.spotify\.com\/episode\/)([^?\s&]+)/,
      /(?:spotify\.com\/episode\/)([^?\s&]+)/,
      // Show URLs (podcasts)
      /(?:open\.spotify\.com\/show\/)([^?\s&]+)/,
      /(?:spotify\.com\/show\/)([^?\s&]+)/,
    ];
    
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match && match[1]) {
        // Return both the ID and the type for proper embedding
        const fullPath = url.match(/(?:open\.)?spotify\.com\/([^?\s&]+)/);
        return fullPath ? fullPath[1] : match[1];
      }
    }
    
    return null;
  }

  getEmbedUrl(pathId) {
    // pathId contains the full path like "track/4iV5W9uYEdYUVa79Axb7Rh" or just the ID
    const cleanPath = pathId.includes('/') ? pathId : `track/${pathId}`;
    return `https://open.spotify.com/embed/${cleanPath}?utm_source=generator&theme=0&autoplay=0&allowtransparency=true`;
  }

  getThumbnailUrl(id) {
    // Spotify doesn't provide direct thumbnail URLs without API
    return null; // Will trigger fallback gradient
  }

  getStyling() {
    return {
      primary: '#1db954',
      accent: '#1ed760',
      gradient: 'linear-gradient(135deg, #1db954, #1ed760, #00d461)'
    };
  }
}

/**
 * Apple Music platform handler
 */
class AppleMusicHandler extends MediaPlatformHandler {
  constructor() {
    super();
    this.platform = 'apple';
  }

  isValidUrl(url) {
    if (!url) return false;
    return /(?:music\.apple\.com|itunes\.apple\.com|embed\.music\.apple\.com)/i.test(url);
  }

  isEmbeddablePath(path) {
    return /\/(album|song|playlist|episode|show|music-video)\//i.test(path) || /[?&]i=\d+/.test(path);
  }

  async extractId(url) {
    if (!url) return null;

    const match = url.match(/(?:music|itunes|embed\.music)\.apple\.com(\/[^#\s]*)/i);
    if (!match) return null;

    const path = match[1];
    if (!this.isEmbeddablePath(path)) return null;

    return path.replace(/^\//, '');
  }

  getEmbedUrl(path) {
    return `https://embed.music.apple.com/${path}`;
  }

  getThumbnailUrl() {
    return null;
  }

  getStyling() {
    return {
      primary: '#fa243c',
      accent: '#fc3c44',
      gradient: 'linear-gradient(135deg, #fa243c, #fc3c44, #ff2d55)'
    };
  }
}

/**
 * Vimeo platform handler
 */
class VimeoHandler extends MediaPlatformHandler {
  constructor() {
    super();
    this.platform = 'vimeo';
  }

  isValidUrl(url) {
    if (!url) return false;
    return /vimeo\.com/i.test(url);
  }

  isProfileUrl(url) {
    return /vimeo\.com\/(channels|user|groups|showcase)/i.test(url);
  }

  async extractId(url) {
    if (!url || this.isProfileUrl(url)) return null;

    const playerMatch = url.match(/player\.vimeo\.com\/video\/(\d+)/i);
    if (playerMatch) return playerMatch[1];

    const videoMatch = url.match(/vimeo\.com\/(?:video\/)?(\d+)/i);
    if (videoMatch) return videoMatch[1];

    return null;
  }

  getEmbedUrl(id) {
    return `https://player.vimeo.com/video/${id}`;
  }

  getThumbnailUrl() {
    return null;
  }

  getStyling() {
    return {
      primary: '#1ab7ea',
      accent: '#00adef',
      gradient: 'linear-gradient(135deg, #1ab7ea, #00adef, #0088cc)'
    };
  }
}

/**
 * Bandcamp platform handler
 */
class BandcampHandler extends MediaPlatformHandler {
  constructor() {
    super();
    this.platform = 'bandcamp';
  }

  isValidUrl(url) {
    if (!url) return false;
    return /bandcamp\.com/i.test(url);
  }

  isEmbeddablePath(url) {
    return /\/(track|album)\//i.test(url);
  }

  async extractId(url) {
    if (!url || !this.isEmbeddablePath(url)) return null;

    try {
      const axiosModule = await import('../api/axiosInstance');
      const axiosInstance = axiosModule.default;
      const { data } = await axiosInstance.get(`/api/bandcamp-oembed?url=${encodeURIComponent(url)}`);
      return data.embedUrl || null;
    } catch (error) {
      console.error('Failed to resolve Bandcamp embed URL:', error);
      return null;
    }
  }

  getEmbedUrl(embedUrl) {
    return embedUrl;
  }

  getThumbnailUrl() {
    return null;
  }

  getStyling() {
    return {
      primary: '#629aa9',
      accent: '#1da0c3',
      gradient: 'linear-gradient(135deg, #629aa9, #1da0c3, #408294)'
    };
  }
}

// ==================== PLATFORM REGISTRY ====================

// Order matters for detection - more specific patterns first
const platformHandlers = {
  spotify: new SpotifyHandler(),
  apple: new AppleMusicHandler(),
  youtube: new YouTubeHandler(),
  vimeo: new VimeoHandler(),
  soundcloud: new SoundCloudHandler(),
  bandcamp: new BandcampHandler(),
};

/**
 * Get the appropriate platform handler for a URL
 * @param {string} url - URL to check
 * @returns {MediaPlatformHandler|null} - Platform handler or null
 */
export const getPlatformHandler = (url) => {
  for (const handler of Object.values(platformHandlers)) {
    if (handler.isValidUrl(url)) {
      return handler;
    }
  }
  return null;
};

/**
 * Get platform name from URL
 * @param {string} url - URL to check
 * @returns {string|null} - Platform name or null
 */
export const getPlatformName = (url) => {
  const handler = getPlatformHandler(url);
  return handler ? handler.platform : null;
};

/**
 * Check if URL is embeddable media
 * @param {string} url - URL to check
 * @returns {boolean} - True if embeddable
 */
export const isEmbeddableMediaUrl = (url) => {
  const handler = getPlatformHandler(url);
  if (!handler) return false;
  
  // SoundCloud: Only tracks/playlists are embeddable, not profiles
  if (handler.platform === 'soundcloud') {
    // Short URLs are embeddable
    if (url.includes('on.soundcloud.com')) {
      return true;
    }
    // Profile URLs (soundcloud.com/username or soundcloud.com/username?params) are NOT embeddable
    const isSoundCloudProfile = /soundcloud\.com\/[^\/\?]+\/?(\?.*)?$/.test(url);
    if (isSoundCloudProfile) {
      return false;
    }
    // Tracks/playlists (soundcloud.com/username/track-name) ARE embeddable
    return true;
  }

  if (handler.platform === 'vimeo') {
    return !handler.isProfileUrl(url) && /(?:player\.)?vimeo\.com\/(?:video\/)?\d+/i.test(url);
  }

  if (handler.platform === 'apple') {
    const pathMatch = url.match(/(?:music|itunes|embed\.music)\.apple\.com(\/[^#\s]*)/i);
    return !!pathMatch && handler.isEmbeddablePath(pathMatch[1]);
  }

  if (handler.platform === 'bandcamp') {
    return handler.isEmbeddablePath(url);
  }
  
  return true;
};

// ==================== BACKWARD COMPATIBILITY ====================

/**
 * @deprecated Use getPlatformHandler(url).isValidUrl(url) instead
 */
export const isYouTubeUrl = (url) => {
  return platformHandlers.youtube.isValidUrl(url);
};

/**
 * @deprecated Use getPlatformHandler(url).isValidUrl(url) instead
 */
export const isSoundCloudUrl = (url) => {
  return platformHandlers.soundcloud.isValidUrl(url);
};

/**
 * @deprecated Use getPlatformHandler(url).isValidUrl(url) instead
 */
export const isSpotifyUrl = (url) => {
  return platformHandlers.spotify.isValidUrl(url);
};

/**
 * @deprecated Use getPlatformName(url) instead
 */
export const getSocialMediaPlatform = (url) => {
  return getPlatformName(url);
};

/**
 * @deprecated Use getPlatformHandler(url).extractId(url) instead
 */
export const extractYouTubeId = (url) => {
  return platformHandlers.youtube.extractId(url);
};

/**
 * @deprecated Use getPlatformHandler(url).getEmbedUrl(id) instead
 */
export const getYouTubeEmbedUrl = (videoId) => {
  return platformHandlers.youtube.getEmbedUrl(videoId);
};

/**
 * @deprecated Use getPlatformHandler(url).getThumbnailUrl(id) instead
 */
export const getYouTubeThumbnail = (videoId, quality) => {
  return platformHandlers.youtube.getThumbnailUrl(videoId, quality);
};
